(() => {
  'use strict';
  const initialize = (root) => {
    if (root.dataset.bpInitialized === 'true') return;
    root.dataset.bpInitialized = 'true';
    const one = (selector) => root.querySelector(selector);
    const all = (selector) => [...root.querySelectorAll(selector)];
    const data = JSON.parse(one('[data-bp-data]').textContent);
    const form = one('.bp-form');
    const variantInput = one('[data-bp-variant]');
    const quantityInput = one('[data-bp-quantity-input]');
    const stickyQuantity = one('[data-bp-sticky-quantity]');
    const submitButtons = all('[data-bp-add]');
    const stage = one('[data-bp-zoom-open]');
    const videoStage = one('[data-bp-video-stage]');
    const templates = all('template[data-bp-image]');
    const thumbs = all('[data-bp-thumb]');
    const lightbox = one('[data-bp-lightbox]');
    const sticky = one('[data-bp-sticky]');
    const purchase = one('[data-bp-primary-purchase]');
    const stickySpacer = document.createElement('div');
    stickySpacer.dataset.bpStickySpacer = root.id;
    stickySpacer.setAttribute('aria-hidden', 'true');
    document.body.append(stickySpacer);
    const controller = new AbortController();
    const signal = controller.signal;
    const listen = (node, type, listener, options = {}) => node?.addEventListener(type, listener, {...options, signal});
    const selected = () => data.variants.find((variant) => String(variant.id) === variantInput.value);
    let galleryIndex = 0;
    let pending = false;
    let opener = null;
    let previousOverflow = '';
    let scrollFrame = null;
    let didSwipe = false;
    let galleryVideo = null;
    const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isVideo = (template) => template?.dataset.bpMediaType === 'video';
    const imageTemplates = templates.filter((template) => !isVideo(template));
    const updateLightboxCount = () => {
      const count = one('[data-bp-lightbox-count]');
      if (count) count.textContent = `${imageTemplates.indexOf(templates[galleryIndex]) + 1} / ${imageTemplates.length}`;
    };
    const showMedia = (index) => {
      if (!templates.length) return;
      const direction = index < galleryIndex ? -1 : 1;
      galleryIndex = (index % templates.length + templates.length) % templates.length;
      // The image lightbox excludes video; the main gallery includes every media item.
      if (lightbox.open) {
        while (isVideo(templates[galleryIndex])) galleryIndex = (galleryIndex + direction + templates.length) % templates.length;
      }
      const template = templates[galleryIndex];
      const showingVideo = isVideo(template);
      if (!showingVideo) galleryVideo?.pause();
      stage.hidden = showingVideo;
      if (videoStage) videoStage.hidden = !showingVideo;
      stage.dataset.bpGalleryIndex = String(galleryIndex);
      if (showingVideo && videoStage) {
        if (!galleryVideo) {
          galleryVideo = template.content.querySelector('video').cloneNode(true);
          galleryVideo.preload = 'none';
          galleryVideo.playsInline = true;
          videoStage.replaceChildren(galleryVideo);
        }
      } else {
        const image = template.content.querySelector('img').cloneNode(true);
        image.loading = 'eager';
        stage.querySelector('img').replaceWith(image);
        if (lightbox.open) one('[data-bp-zoom-image]').replaceChildren(image.cloneNode(true));
      }
      thumbs.forEach((button, current) => button.setAttribute('aria-pressed', String(current === galleryIndex)));
      all('[data-bp-gallery-count]').forEach((count) => { count.textContent = `${galleryIndex + 1} / ${templates.length}`; });
      one('[data-bp-gallery-caption]').textContent = galleryIndex === 0 ? data.heroCaption || '' : template.dataset.caption || '';
      if (lightbox.open) updateLightboxCount();
    };
    thumbs.forEach((button) => listen(button, 'click', () => { didSwipe = false; showMedia(Number(button.dataset.bpThumb)); }));
    all('[data-bp-gallery-prev]').forEach((button) => listen(button, 'click', () => { didSwipe = false; showMedia(galleryIndex - 1); }));
    all('[data-bp-gallery-next]').forEach((button) => listen(button, 'click', () => { didSwipe = false; showMedia(galleryIndex + 1); }));
    listen(one('[data-bp-watch-film]'), 'click', () => {
      const index = templates.findIndex(isVideo);
      if (index >= 0) showMedia(index);
      // Keep the link's native fragment navigation to the top gallery.
    });
    listen(document, 'visibilitychange', () => { if (document.hidden) galleryVideo?.pause(); });
    const galleryKeys = (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      showMedia(galleryIndex + (event.key === 'ArrowRight' ? 1 : -1));
    };
    listen(stage, 'keydown', galleryKeys);
    listen(lightbox, 'keydown', galleryKeys);
    let touchStart;
    listen(stage, 'touchstart', (event) => {
      didSwipe = false;
      if (event.touches.length === 1) touchStart = {x:event.touches[0].clientX, y:event.touches[0].clientY};
    }, {passive:true});
    listen(stage, 'touchend', (event) => {
      if (!touchStart || !event.changedTouches.length) return;
      const dx = event.changedTouches[0].clientX - touchStart.x;
      const dy = event.changedTouches[0].clientY - touchStart.y;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        didSwipe = true;
        showMedia(galleryIndex + (dx < 0 ? 1 : -1));
      }
      touchStart = null;
    }, {passive:true});
    listen(stage, 'click', () => {
      if (didSwipe) { didSwipe = false; return; }
      if (isVideo(templates[galleryIndex])) return;
      opener = document.activeElement;
      previousOverflow = document.body.style.overflow;
      one('[data-bp-zoom-image]').replaceChildren(stage.querySelector('img').cloneNode(true));
      updateLightboxCount();
      lightbox.showModal();
      document.body.style.overflow = 'hidden';
      one('[data-bp-zoom-close]').focus();
    });
    listen(one('[data-bp-zoom-close]'), 'click', () => lightbox.close());
    listen(lightbox, 'close', () => { document.body.style.overflow = previousOverflow; opener?.focus(); });

    const syncQuantity = (raw = quantityInput.value) => {
      const variant = selected();
      const minimum = variant?.min || 1;
      const increment = variant?.step || 1;
      const maximum = variant?.max || Infinity;
      let quantity = Number(raw);
      if (!Number.isSafeInteger(quantity)) quantity = minimum;
      quantity = Math.min(maximum, Math.max(minimum, quantity));
      quantity = minimum + Math.floor((quantity - minimum) / increment) * increment;
      [quantityInput, stickyQuantity].forEach((input) => {
        input.value = String(quantity);
        input.min = String(minimum);
        input.step = String(increment);
        if (Number.isFinite(maximum)) input.max = String(maximum); else input.removeAttribute('max');
      });
      all('[data-bp-quantity]').forEach((button) => {
        const delta = Number(button.dataset.bpQuantity);
        button.disabled = pending || !variant?.available || (delta < 0 ? quantity <= minimum : quantity >= maximum);
      });
      return quantity;
    };
    all('[data-bp-quantity]').forEach((button) => listen(button, 'click', () => {
      syncQuantity(syncQuantity() + Number(button.dataset.bpQuantity) * (selected()?.step || 1));
    }));
    listen(quantityInput, 'change', () => syncQuantity());
    listen(stickyQuantity, 'change', () => syncQuantity(stickyQuantity.value));
    const message = (text = '', error = false) => {
      ['[data-bp-message]', '[data-bp-sticky-message]'].forEach((selector) => {
        const target = one(selector);
        target.textContent = text;
        target.hidden = !text;
        target.classList.toggle('is-error', error);
      });
    };
    const buttonState = () => {
      const variant = selected();
      submitButtons.forEach((button) => {
        button.disabled = pending || !variant?.available;
        if (pending) button.setAttribute('aria-busy', 'true'); else button.removeAttribute('aria-busy');
        button.querySelector('[data-bp-add-label]').textContent = pending ? 'ADDING…' : variant?.available ? 'ADD TO BAG' : 'SOLD OUT';
      });
      quantityInput.disabled = pending || !variant?.available;
      stickyQuantity.disabled = pending || !variant?.available;
      variantInput.disabled = pending;
      syncQuantity();
    };
    const updateVariant = (updateUrl = true) => {
      const variant = selected();
      if (!variant) return;
      one('[data-bp-price]').innerHTML = variant.price;
      one('[data-bp-sticky-price]').innerHTML = variant.price;
      one('[data-bp-compare]').innerHTML = variant.compare;
      one('[data-bp-compare]').hidden = !variant.onSale;
      one('[data-bp-stock]').textContent = variant.available ? 'In stock' : 'Sold out';
      one('[data-bp-sticky-variant]').textContent = variant.title === 'Default Title' ? '' : variant.title;
      const sku = one('[data-bp-sku]');
      sku.textContent = variant.sku ? `Product code: ${variant.sku}` : '';
      sku.hidden = !variant.sku;
      buttonState();
      if (updateUrl) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
        message();
        one('[data-bp-cart-link]').hidden = true;
      }
      root.dispatchEvent(new CustomEvent('sopami:variantchange', {bubbles:true, detail:{productId:data.id, variant}}));
    };
    listen(variantInput, 'change', () => updateVariant());
    listen(window, 'popstate', () => {
      const id = new URL(location.href).searchParams.get('variant');
      if (id && data.variants.some((variant) => String(variant.id) === id)) { variantInput.value = id; updateVariant(false); }
    });
    listen(form, 'submit', async (event) => {
      if (!window.fetch) return;
      event.preventDefault();
      if (pending) return;
      if (!selected()?.available) { message('This option is sold out. Please choose another option.', true); return; }
      syncQuantity();
      if (!form.reportValidity()) return;
      const payload = new FormData(form);
      pending = true;
      buttonState();
      message();
      one('[data-bp-cart-link]').hidden = true;
      try {
        const response = await fetch(root.dataset.addUrl, {method:'POST', body:payload, credentials:'same-origin', headers:{Accept:'application/json','X-Requested-With':'XMLHttpRequest'}});
        const result = await response.json();
        if (signal.aborted) return;
        if (!response.ok || result.status >= 400) throw new Error(result.description || result.message || 'This item could not be added. Please try again.');
        let cart = null;
        try {
          const cartResponse = await fetch(`${root.dataset.cartUrl}.js`, {credentials:'same-origin', headers:{Accept:'application/json'}, signal});
          if (cartResponse.ok) cart = await cartResponse.json();
        } catch (_) { /* The confirmed add remains successful if cart refresh fails. */ }
        if (signal.aborted) return;
        message('Added to your bag.');
        one('[data-bp-cart-link]').hidden = false;
        if (cart && Number.isInteger(cart.item_count)) document.dispatchEvent(new CustomEvent('sopami:cartchange', {detail:cart}));
        else document.querySelector('[data-jj-open="bag"]')?.click();
      } catch (error) {
        if (signal.aborted) return;
        const uncertain = error instanceof TypeError || error instanceof SyntaxError;
        message(uncertain ? 'Please check your bag before trying again. We could not confirm the result.' : error.message || 'This item could not be added. Please try again.', true);
        one('[data-bp-cart-link]').hidden = !uncertain;
      } finally {
        pending = false;
        if (!signal.aborted) buttonState();
      }
    });

    const featureTrack = one('[data-bp-feature-track]');
    const updateProgress = () => {
      if (!featureTrack) return;
      const ratio = Math.min(1, featureTrack.clientWidth / featureTrack.scrollWidth);
      const progress = one('[data-bp-feature-progress]');
      progress.style.width = `${ratio * 100}%`;
      progress.style.transform = `translateX(${featureTrack.scrollLeft / featureTrack.clientWidth * 100}%)`;
      one('[data-bp-feature-next]').disabled = featureTrack.scrollLeft + featureTrack.clientWidth >= featureTrack.scrollWidth - 2;
    };
    listen(featureTrack, 'scroll', updateProgress, {passive:true});
    listen(one('[data-bp-feature-next]'), 'click', () => featureTrack.scrollBy({left:featureTrack.clientWidth * .85, behavior:reducedMotion() ? 'auto' : 'smooth'}));
    const updateSticky = () => {
      scrollFrame = null;
      const header = document.querySelector('#shopify-section-jj-header');
      const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
      sticky.hidden = purchase.getBoundingClientRect().bottom > headerBottom;
      stickySpacer.style.height = sticky.hidden ? '0px' : `${sticky.offsetHeight + 25}px`;
    };
    const requestSticky = () => { if (!signal.aborted && scrollFrame === null) scrollFrame = requestAnimationFrame(updateSticky); };
    listen(window, 'scroll', requestSticky, {passive:true});
    listen(window, 'resize', () => { updateProgress(); requestSticky(); }, {passive:true});
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(requestSticky) : null;
    resizeObserver?.observe(purchase);
    resizeObserver?.observe(sticky);
    listen(document, 'shopify:section:unload', (event) => {
      if (!event.target.contains(root)) return;
      all('video').forEach((video) => video.pause());
      if (lightbox.open) {
        // The close event is queued; restore scrolling before aborting its listener.
        document.body.style.overflow = previousOverflow;
        lightbox.close();
      }
      controller.abort();
      resizeObserver?.disconnect();
      if (scrollFrame !== null) cancelAnimationFrame(scrollFrame);
      stickySpacer.remove();
      delete root.dataset.bpInitialized;
    });
    updateVariant(false);
    updateProgress();
    requestSticky();
  };
  const mount = (scope = document) => {
    if (scope.matches?.('[data-bucket-pdp]')) initialize(scope);
    scope.querySelectorAll('[data-bucket-pdp]').forEach(initialize);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount()); else mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
